import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserSettings } from '../utils/user-settings';
import { useWorkspaceStore } from './workspace.store';

export type UserRole = 'admin' | 'media_buyer' | 'viewer' | 'partner';

export interface WorkspaceRef {
  id: string;
  name: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  workspaces: WorkspaceRef[];
  workspace_ids?: string[];
  /** Persisted so report defaults are readable on the first render. */
  settings?: UserSettings;
  /** The user's own birthday (YYYY-MM-DD), self-set in settings. */
  birthday?: string | null;
  /** Teammates (sharing a workspace) whose birthday is today. */
  birthdays_today?: BirthdayToday[];
}

export interface BirthdayToday {
  id: string;
  first_name: string | null;
  last_name: string | null;
  age: number;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, accessToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  /** Reflect a settings save without a re-login, when a user edits their own row. */
  setUserSettings: (settings: UserSettings) => void;
  /** Reflect a birthday save without a re-login. */
  setUserBirthday: (birthday: string | null) => void;
  setUserWorkspaces: (workspaces: WorkspaceRef[]) => void;
  logout: () => void;
}

/**
 * Auth state persisted in localStorage.
 *
 * The long-lived refresh token is NOT stored here - it lives in an
 * httpOnly `__session` cookie set by the API and is only sent by the
 * browser on same-origin requests to /api/v1/auth/refresh.
 *
 * The access token is NOT persisted either — keeping a bearer token out of
 * localStorage, and forcing every page load to re-read the user from the API.
 * `user` and `isAuthenticated` are, so /login can redirect without a round-trip.
 */
/** A selection on a workspace that left the list (archived) would keep filtering every read, unseen. */
function pruneSelection(workspaces: WorkspaceRef[]): void {
  const valid = new Set(workspaces.map((w) => w.id));
  const ws = useWorkspaceStore.getState();
  if (ws.selectedIds.some((id) => !valid.has(id))) ws.setSelectedIds(ws.selectedIds.filter((id) => valid.has(id)));
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      setAuth: (user, accessToken) => {
        pruneSelection(user.workspaces ?? []);
        set({ user, accessToken, isAuthenticated: true });
      },
      setAccessToken: (accessToken) =>
        set({ accessToken }),
      setUserSettings: (settings) =>
        set((state) => (state.user ? { user: { ...state.user, settings } } : {})),
      setUserBirthday: (birthday) =>
        set((state) => (state.user ? { user: { ...state.user, birthday } } : {})),
      setUserWorkspaces: (workspaces) => {
        pruneSelection(workspaces);
        set((state) => (state.user ? { user: { ...state.user, workspaces } } : {}));
      },
      logout: () => {
        useWorkspaceStore.getState().clear();
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'metaxi-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
